// src/components/organizer/auth/OrganizerApply.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { applyOrganizer, resetApplicationState } from "@/store/slices/organizerSlice";
import { 
  Building2, Mail, Phone, MapPin, FileText, Clock, 
  Users, Plus, Trash2, CheckCircle, AlertCircle 
} from "lucide-react";
import ParticleBackground from "@/components/home/ParticleBackground";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import TextArea from "@/components/common/TextArea";
import SelectDropdown from "@/components/common/SelectDropdown";
import CityAutocomplete from "@/components/common/CityAutocomplete";

const REGISTRATION_TYPES = [
  { value: "ngo_trust", label: "NGO / Trust" },
  { value: "society", label: "Society" },
  { value: "company", label: "Company" },
  { value: "unregistered", label: "Unregistered" },
];

const OrganizerApply = () => {
  const dispatch = useDispatch();
  const { isLoading, error, applicationSubmitted } = useSelector((state) => state.organizer);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      organization_name: "",
      applicant_email: "",
      applicant_phone: "",
      city: "",
      registration_type: "",
      registration_number: "",
      shareholder_profession: "",
      cycling_history: "",
      time_commitment: "",
      team_members: [{ name: "", about: "", experience: "", phone: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "team_members",
  });

  const registrationType = watch("registration_type");

  React.useEffect(() => {
    return () => {
      dispatch(resetApplicationState());
    };
  }, [dispatch]);

  const onSubmit = async (data) => {
    if (!declarationAccepted) return;
    
    const applicationData = {
      ...data,
      declaration_accepted: true,
    };
    
    dispatch(applyOrganizer(applicationData));
  };

  if (applicationSubmitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center py-12 px-4 overflow-hidden">
        <ParticleBackground />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative z-10 max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 text-center"
        >
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Application Submitted!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your application has been submitted successfully! Our team will review it and get back to you within 3-5 business days. Once approved, you can login using the credentials sent to your email.
          </p>
          <Link
            to="/organizer/login"
            className="inline-flex items-center text-amber-600 hover:text-amber-700 dark:text-amber-400 font-semibold"
          >
            Already approved? Login here →
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-12 px-4 overflow-hidden">
      <ParticleBackground />
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 md:p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Become an Organizer
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Apply to organize cycling events on Koset
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Organization Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Organization Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Organization Name *"
                placeholder="Enter organization name"
                error={errors.organization_name?.message}
                {...register("organization_name", { required: "Organization name is required" })}
              />
              <Controller
                name="city"
                control={control}
                rules={{ required: "City is required" }}
                render={({ field }) => (
                  <CityAutocomplete
                    label="City"
                    required
                    placeholder="Enter city"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.city?.message}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Controller
                name="registration_type"
                control={control}
                rules={{ required: "Registration type is required" }}
                render={({ field }) => (
                  <SelectDropdown
                    label="Registration Type *"
                    options={REGISTRATION_TYPES}
                    placeholder="Select registration type"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={errors.registration_type?.message}
                  />
                )}
              />
              {registrationType && registrationType !== "unregistered" && (
                <TextInput
                  label="Registration Number"
                  placeholder="Enter registration number"
                  {...register("registration_number")}
                />
              )}
            </div>
          </section>

          {/* Contact Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Contact Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Email Address *"
                type="email"
                icon={<Mail className="w-5 h-5" />}
                placeholder="contact@organization.com"
                error={errors.applicant_email?.message}
                {...register("applicant_email", {
                  required: "Email is required",
                  pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email" },
                })}
              />
              <TextInput
                label="Phone Number *"
                icon={<Phone className="w-5 h-5" />}
                placeholder="9876543210"
                error={errors.applicant_phone?.message}
                {...register("applicant_phone", {
                  required: "Phone is required",
                  pattern: { value: /^[6-9]\d{9}$/, message: "Please provide a valid 10-digit Indian phone number" },
                })}
              />
            </div>
          </section>

          {/* Background */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Background
            </h3>
            <div className="space-y-4">
              <TextInput
                label="Key Contact's Profession *"
                placeholder="e.g., Software Engineer, Business Owner"
                error={errors.shareholder_profession?.message}
                {...register("shareholder_profession", { required: "Profession is required" })}
              />
              <TextArea
                label="Cycling History *"
                placeholder="Describe your organization's experience with cycling events..."
                rows={4}
                error={errors.cycling_history?.message}
                {...register("cycling_history", {
                  required: "Cycling history is required",
                  minLength: { value: 20, message: "Please provide at least 20 characters about your cycling background" },
                })}
              />
              <TextInput
                label="Time Commitment *"
                icon={<Clock className="w-5 h-5" />}
                placeholder="e.g., 10-15 hours per week"
                error={errors.time_commitment?.message}
                {...register("time_commitment", { required: "Time commitment is required" })}
              />
            </div>
          </section>

          {/* Team Members */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Team Members
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ name: "", about: "", experience: "", phone: "" })}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Member
              </Button>
            </div>
            
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Member {index + 1}
                    </span>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextInput
                      label="Name *"
                      placeholder="Member name"
                      error={errors.team_members?.[index]?.name?.message}
                      {...register(`team_members.${index}.name`, { required: "Name is required" })}
                    />
                    <TextInput
                      label="Phone"
                      placeholder="Phone number"
                      error={errors.team_members?.[index]?.phone?.message}
                      {...register(`team_members.${index}.phone`, {
                        pattern: { value: /^[6-9]\d{9}$/, message: "Please provide a valid 10-digit Indian phone number" },
                      })}
                    />
                    <TextInput
                      label="Role/About"
                      placeholder="e.g., Founder, Event Manager"
                      {...register(`team_members.${index}.about`)}
                    />
                    <TextInput
                      label="Experience"
                      placeholder="e.g., 5 years"
                      {...register(`team_members.${index}.experience`)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Declaration */}
          <section className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={declarationAccepted}
                onChange={(e) => setDeclarationAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                I declare that all information provided is accurate and I agree to the terms and conditions of organizing events on Koset platform. *
              </span>
            </label>
          </section>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </motion.div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!declarationAccepted}
              className="flex-1 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:opacity-50"
              size="lg"
            >
              Submit Application
            </Button>
            <Link
              to="/organizer/login"
              className="flex-1 py-3 text-center border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Already have an account?
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default OrganizerApply;
